import React, { useEffect, useState } from 'react';
import {
  Player,
  Mode,
  ChatMessage,
  Spectator,
} from '../types';
import { SpectatePlayerInput } from './player-input/SpectatePlayerInput';
import { GameHeader } from './GameHeader';
import { QuestionAnswer } from './question-answer/QuestionAnswer';
import { PlayerInput } from './player-input/PlayerInput';
import { CorrectAnswer } from './CorrectAnswer';
import { ScoreBar } from './score-bar';
import { Socket } from 'socket.io-client';
import {
  Stack,
  Box,
  Paper,
  Typography,
  Container,
  Avatar,
  Button,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Divider,
  ListItemAvatar,
  Drawer,
  useTheme,
} from '@mui/material';
import './styles.css';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { PoolPlayLeaderboard } from './PoolPlayLeaderboard';
import ChatIcon from '@mui/icons-material/Chat';
import { DraggableChat } from '../../../dialog/DraggableChat';
import MarkUnreadChatAltIcon from '@mui/icons-material/MarkUnreadChatAlt';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { useNavigate } from 'react-router-dom';
import { resizeImage } from '../../../api/cloudinary';
import CircleIcon from '@mui/icons-material/Circle';
import { FrameRate } from './FrameRate';
import { Latency } from './Latency';
import { Penalty, ProperNameShortAnswer } from '@par/shared/types/game';
import { TournamentSitAndGoLeaderboard } from './TournamentSitAndGoLeaderboard';
import { sortPlayers } from '..';

export const poolLeaderboardDrawerWidth = 300;
export const poolLeaderboardDrawerWidthMobile = 144;

type PropTypes = {
  answer?: string[][];
  category: string;
  categorySet: string;
  clearAfterPenalty?: boolean;
  cutoff?: number;
  delayBeforeQuestionRevealCount: number;
  displaySize: 'sm' | 'md' | 'lg';
  freezeGameClock: boolean;
  fullQuestion?: string;
  fuzzy?: Record<string, number>;
  gameClock: number;
  gameId: string;
  gameOver: boolean;
  gameType: 'casual' | 'pro';
  isAdmin: boolean;
  mathFormula?: string;
  mode: Mode;
  nextQuestionDelayCount: number;
  par: number;
  penalty?: Penalty;
  playerSelf: Player;
  players: Record<string, Player>;
  profileId: string;
  properNameShortAnswer?: ProperNameShortAnswer;
  question: string;
  questionImgUrl?: string;
  questionIndex: number;
  rejoin: boolean;
  showConnectionStats?: boolean;
  socket?: Socket;
  spectate: boolean;
  spectateGameId?: string;
  spectatorCount: number;
  spectatorList: Spectator[];
  stopSpectating: (isPopState: boolean) => void;
  timerLimitReached: boolean;
  tournamentTotalPlayers?: number;
  totalNumQuestions: number | undefined;
  tournament?: number;
  unrevealedIndex?: number[];
  username: string;
};

export const TournamentGame = ({
  answer,
  cutoff,
  category,
  categorySet,
  clearAfterPenalty,
  delayBeforeQuestionRevealCount,
  displaySize,
  freezeGameClock,
  fullQuestion,
  fuzzy,
  gameClock,
  gameId,
  gameOver,
  gameType,
  isAdmin,
  mathFormula,
  mode,
  nextQuestionDelayCount,
  par,
  penalty,
  playerSelf,
  players,
  profileId,
  properNameShortAnswer,
  question,
  questionImgUrl,
  questionIndex,
  rejoin,
  showConnectionStats,
  socket,
  spectate,
  spectateGameId,
  spectatorCount,
  spectatorList,
  stopSpectating,
  timerLimitReached,
  tournamentTotalPlayers,
  totalNumQuestions,
  tournament,
  unrevealedIndex,
  username,
}: PropTypes) => {
  const navigate = useNavigate();
  const [sortedPlayers, setSortedPlayers] = useState<Player[]>();
  const [displayGameClock, setDisplayGameClock] = useState(false);
  const [selectedProfileIds, setSelectedProfileIds] = useState<string[]>(
    players
      ? Object.values(players)
          .slice(0, 8)
          .map((p) => p.profileId)
      : []
  );
  const [spectatorChat, setSpectatorChat] = useState<ChatMessage[]>();
  const [openSpectatorChat, setOpenSpectatorChat] = useState(false);
  const [spectatorChatLastSeen, setSpectatorChatLastSeen] = useState<number>();
  const [unreadSpectatorChat, setUnreadSpectatorChat] = useState<boolean>();

  useEffect(() => {
    const sortedPlayers = sortPlayers(players);
    setSortedPlayers(sortedPlayers);
  }, [players]);

  useEffect(() => {
    if (!socket) return;
    const keyDownHandler = (event: any) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCloseSpectatorChat();
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        handleOpenSpectatorChat();
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [socket]);

  useEffect(() => {
    if (!playerSelf) return;
    // this is not pretty, could probably handle this better from the serve
    if (
      nextQuestionDelayCount <= 3 &&
      (timerLimitReached || playerSelf.correctAnswer)
    ) {
      setTimeout(() => {
        setDisplayGameClock(false);
      }, 200);
      return;
    }
    if (
      delayBeforeQuestionRevealCount === 1 &&
      !timerLimitReached &&
      !playerSelf.correctAnswer
    ) {
      setDisplayGameClock(false);
      return;
    }
    if (delayBeforeQuestionRevealCount <= 0) {
      setDisplayGameClock(true);
    }
  }, [
    delayBeforeQuestionRevealCount,
    nextQuestionDelayCount,
    timerLimitReached,
    playerSelf,
  ]);

  useEffect(() => {
    if (!spectate) return;
    setDisplayGameClock(true);
    socket?.on('spectatorChat', reverseSpectatorChat);
  }, [spectate]);

  useEffect(() => {
    if (!rejoin) return;
    setDisplayGameClock(true);
  }, [rejoin]);

  useEffect(() => {
    if (!spectatorChat) return;
    const recentMessageCreatedAt =
      spectatorChat[spectatorChat.length - 1]?.createdAt || 0;
    if (
      !openSpectatorChat &&
      recentMessageCreatedAt > (spectatorChatLastSeen || 0)
    ) {
      setUnreadSpectatorChat(true);
    }
  }, [spectatorChat]);

  const theme = useTheme();

  const playerMeAnsweredFirst = () => {
    if (!playerSelf) return false;
    const playerMeScore = playerSelf.currentScore || 0;
    if (playerMeScore !== undefined && mode === 'sit-n-go') return true;
    return false;
  };

  const displayOpponentTimer = () => {
    if (mode === 'sit-n-go') {
      return freezeGameClock && playerMeAnsweredFirst() && !spectate;
    }
    return false;
  };

  const handlePoolLeaderboardUsernameClick = (profileId: string) => {
    if (spectate && !selectedProfileIds.includes(profileId)) {
      if (selectedProfileIds.length < 8) {
        setSelectedProfileIds((prev) => prev.concat(profileId));
      } else {
        setSelectedProfileIds((prev) => prev.slice(0, 7).concat(profileId));
      }
    }
  };

  const deselectPlayer = (profileId: string) => {
    if (spectate) {
      const updated = selectedProfileIds.filter((pid) => pid !== profileId);
      setSelectedProfileIds(updated);
    }
  };

  const handleSelectedProfileIdsFilter = (
    filter: 'removeAll' | 'top4' | 'top8' | 'cutoff'
  ) => {
    if (filter === 'removeAll') {
      setSelectedProfileIds([]);
      return;
    }
    if (filter === 'top4') {
      const top4 = sortedPlayers.slice(0, 4).map((p) => p.profileId);
      setSelectedProfileIds(top4);
      return;
    }
    if (filter === 'top8') {
      const top8 = sortedPlayers.slice(0, 8).map((p) => p.profileId);
      setSelectedProfileIds(top8);
      return;
    }
    if (filter === 'cutoff') {
      const cutoffLine = calcCutoffLine();
      const fiveToTwelve = sortedPlayers
        .slice(cutoffLine - 4, cutoffLine + 4)
        .map((p) => p.profileId);
      setSelectedProfileIds(fiveToTwelve);
      return;
    }
  };

  const handleOpenSpectatorChat = () => {
    if (!spectate) return;
    setOpenSpectatorChat(true);
    setUnreadSpectatorChat(false);
  };

  const handleCloseSpectatorChat = () => {
    if (!spectate) return;
    setOpenSpectatorChat(false);
    if (!spectatorChat) return;
    setSpectatorChatLastSeen(
      spectatorChat[spectatorChat.length - 1]?.createdAt
    );
  };

  const reverseSpectatorChat = (spectatorChat: ChatMessage[]) => {
    const rev = spectatorChat.sort(
      (a, b) => (a.createdAt || 0) - (b.createdAt || 0)
    );
    setSpectatorChat(rev);
  };

  const handleExitSpectate = () => {
    stopSpectating(false);
    navigate('/lobby');
  };

  const calcCutoffLine = () => {
    if (!cutoff || !sortedPlayers) return;
    const cutoffLine = sortedPlayers?.filter(
      (p) => p.rank && p.rank <= cutoff
    ).length;
    return (cutoffLine || 0) - 1;
  };

  const scoreBars = playerSelf ? (
    <ScoreBar
      avatar={playerSelf.avatarUrl}
      mode={mode}
      score={playerSelf.parScore}
      username={playerSelf.username}
      usernameLocation={'left'}
    />
  ) : null;

  const spectatorsTooltip = (
    <>
      <Typography variant="h6">Spectators</Typography>
      <Divider />
      {tournament ? (
        <Stack mt={2}>
          {spectatorList?.map((s) => (
            <Typography sx={{ color: theme.palette.dailies.main }}>
              {s.username}
            </Typography>
          ))}
        </Stack>
      ) : (
        <List
          sx={{
            bgcolor: 'background.paper',
            position: 'relative',
            overflow: 'auto',
            maxHeight: 300,
          }}
        >
          {spectatorList?.map((s) => (
            <ListItem>
              <ListItemAvatar>
                <Avatar
                  alt={s.username}
                  src={resizeImage(s.avatarPublicId, 50, 50)}
                />
              </ListItemAvatar>
              <ListItemText primary={s.username} />
            </ListItem>
          ))}
        </List>
      )}
    </>
  );

  const spectatorChatTooltip = (
    <Stack>
      <Typography variant="body1">[Enter] open chat</Typography>
      <Typography variant="body1">[ESC] close chat</Typography>
    </Stack>
  );

  const exitSpectateTooltip = (
    <Typography variant="body1">Leave spectate</Typography>
  );

  return (
    <>
      <DraggableChat
        chat={spectatorChat}
        closeChat={handleCloseSpectatorChat}
        gameId={spectateGameId}
        openChat={openSpectatorChat}
        profileId={profileId}
        socket={socket}
        socketEmitEvent={'spectatorChat'}
        title="SPECTATOR CHAT"
        username={username}
      />
      <Box>
        {showConnectionStats && !gameOver ? (
          <Stack
            sx={{
              position: 'absolute',
              top: '10px',
              right:
                mode === 'sit-n-go'
                  ? `${poolLeaderboardDrawerWidth + 10}px`
                  : '10px',
            }}
          >
            <Stack direction={'row'} spacing={2}>
              {!socket?.connected ? (
                <Typography variant={'caption'} color="warning.main">
                  Reconnecting...
                </Typography>
              ) : (
                <Typography variant={'caption'} color="success.main">
                  Connected:
                </Typography>
              )}
              <Tooltip
                title={`Connection status: ${
                  socket?.connected
                    ? 'connected'
                    : 'disconnected'
                }`}
                placement="bottom-start"
              >
                <Box>
                  <CircleIcon
                    sx={{
                      fontSize: '16px',
                      color:
                        socket?.connected
                          ? 'green'
                          : 'red',
                    }}
                  />
                </Box>
              </Tooltip>
            </Stack>
            <FrameRate></FrameRate>
            <Latency socket={socket} profileId={profileId}></Latency>
          </Stack>
        ) : null}
        <Box
          sx={{
            width: {
              xs:
                mode === 'sit-n-go'
                  ? `calc(100% - ${poolLeaderboardDrawerWidthMobile}px)`
                  : '100%',
              sm:
                mode === 'sit-n-go'
                  ? `calc(100% - ${poolLeaderboardDrawerWidth}px)`
                  : '100%',
            },
          }}
        >
          {/* <div className="score-bar-username">{playerMe.username}</div> */}
          <Container
            sx={{
              position: 'sticky',
              top: 0,
              backgroundColor: theme.palette.background.default + 'e6',
            }}
          >
            {spectate && mode === 'sit-n-go' ? null : scoreBars}
            <Stack spacing={1}>
              <GameHeader
                category={category}
                categorySet={categorySet}
                delayBeforeQuestionRevealCount={delayBeforeQuestionRevealCount}
                displayGameClock={displayGameClock}
                freezeGameClock={freezeGameClock}
                gameClock={gameClock}
                gameType={gameType}
                goToNextQuestionEarly={!!fullQuestion} // if fullQuestion was received, that means all players answered correctly and will now move to the next question before the 20 sec mark
                mode={mode}
                nextQuestionDelayCount={nextQuestionDelayCount}
                par={par}
                penalty={penalty}
                playerSelf={playerSelf}
                questionIndex={questionIndex}
                socket={socket}
                timerLimitReached={timerLimitReached}
                totalNumQuestions={totalNumQuestions}
                tournament={tournament}
                spectate={spectate}
              />
              <Typography
                className={`opponent-timer ${
                  displayOpponentTimer() ? 'fade-in' : ''
                }`}
                sx={{
                  visibility: displayOpponentTimer() ? 'visible' : 'hidden',
                }}
              >
                Opponent's timer 00:{gameClock < 10 ? '0' : null}
                {gameClock || 0}
              </Typography>
            </Stack>
          </Container>
          <Container sx={{ height: '300px' }}>
            <QuestionAnswer
              answer={answer}
              correctAnswer={playerSelf?.correctAnswer}
              displaySize={displaySize}
              fullQuestion={fullQuestion}
              mathFormula={mathFormula}
              mode={mode}
              properNameShortAnswer={properNameShortAnswer}
              question={question}
              questionImgUrl={questionImgUrl}
              questionIndex={questionIndex}
              spectate={spectate}
              timerLimitReached={timerLimitReached}
              unrevealedIndex={unrevealedIndex}
            ></QuestionAnswer>
          </Container>
          <Stack
            spacing={1}
            sx={{
              width: 'fit-content',
              marginTop: '-140px',
              position: 'fixed',
              zIndex: 100,
            }}
          >
            {spectate && (spectatorList?.length > 0 || spectatorCount > 0) ? (
              <Tooltip title={exitSpectateTooltip} placement="right">
                <Button
                  variant="outlined"
                  startIcon={<ExitToAppIcon />}
                  onClick={handleExitSpectate}
                ></Button>
              </Tooltip>
            ) : null}
            {spectatorList?.length > 0 || spectatorCount > 0 ? (
              <Tooltip
                title={spectatorList?.length ? spectatorsTooltip : null}
                placement="right"
              >
                <Button variant="outlined" startIcon={<VisibilityIcon />}>
                  {spectatorList?.length || spectatorCount}
                </Button>
              </Tooltip>
            ) : null}
            {spectate && (spectatorList?.length > 0 || spectatorCount > 1) ? (
              <Tooltip title={spectatorChatTooltip} placement="right">
                <Button
                  variant="outlined"
                  startIcon={
                    unreadSpectatorChat ? (
                      <MarkUnreadChatAltIcon sx={{ color: 'info.main' }} />
                    ) : (
                      <ChatIcon
                        sx={{
                          color: 'primary.main',
                        }}
                      />
                    )
                  }
                  onClick={handleOpenSpectatorChat}
                ></Button>
              </Tooltip>
            ) : null}
          </Stack>
          <Paper
            elevation={6}
            sx={{
              height: {
                xs: 300,
                sm: `${spectate && mode === 'sit-n-go' ? '18rem' : '8rem'}`,
              },
              width: { xs: '100vw', sm: '100%' },
              position: 'sticky',
              bottom: 0,
              zIndex: { xs: '10', sm: null },
            }}
          >
            {spectate && mode === 'sit-n-go' ? (
              <Stack
                direction={'row'}
                spacing={2}
                sx={{ position: 'absolute' }}
              >
                <Button
                  variant="text"
                  onClick={() => handleSelectedProfileIdsFilter('removeAll')}
                >
                  REMOVE ALL
                </Button>
                <Button
                  variant="text"
                  onClick={() => handleSelectedProfileIdsFilter('top4')}
                >
                  TOP 4
                </Button>
                {Object.keys(players).length > 4 ? (
                  <Button
                    variant="text"
                    onClick={() => handleSelectedProfileIdsFilter('top8')}
                  >
                    TOP 8
                  </Button>
                ) : null}
                {Object.keys(players).length > 4 ? (
                  <Button
                    variant="text"
                    onClick={() => handleSelectedProfileIdsFilter('cutoff')}
                  >
                    CUTOFF
                  </Button>
                ) : null}
              </Stack>
            ) : null}
            <Stack
              spacing={0}
              alignItems="center"
              justifyContent="center"
              height={'100%'}
            >
              {!spectate && playerSelf ? (
                <>
                  <PlayerInput
                    answer={answer}
                    category={category}
                    clearAfterPenalty={clearAfterPenalty}
                    correctAnswer={playerSelf.correctAnswer}
                    fuzzy={fuzzy}
                    gameId={gameId}
                    mode={mode}
                    penalty={penalty}
                    playerSelf={playerSelf}
                    questionIndex={questionIndex}
                    socket={socket}
                    spectate={spectate}
                    timerLimitReached={timerLimitReached}
                  ></PlayerInput>
                  <CorrectAnswer player={playerSelf}></CorrectAnswer>
                </>
              ) : (
                <SpectatePlayerInput
                  answer={answer}
                  deselectPlayer={deselectPlayer}
                  fuzzy={fuzzy}
                  isAdmin={isAdmin}
                  mode={mode}
                  players={players}
                  penalty={penalty}
                  questionIndex={questionIndex}
                  selectedProfileIds={selectedProfileIds}
                  socket={socket}
                  timerLimitReached={timerLimitReached}
                />
              )}
            </Stack>
          </Paper>
        </Box>
        {tournament && tournamentTotalPlayers <= 20 && mode === 'sit-n-go' ? (
          <Drawer
            sx={{
              width: { xs: 144, sm: poolLeaderboardDrawerWidth },
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: { xs: 144, sm: poolLeaderboardDrawerWidth },
                boxSizing: 'border-box',
                bgcolor: '#333333',
                maxHeight: { xs: 416, sm: '100%' },
              },
            }}
            variant="permanent"
            anchor="right"
          >
            <Typography
              variant="h4"
              textAlign="center"
              letterSpacing={3}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              LEADERBOARD
            </Typography>
            <Typography
              variant="h6"
              textAlign="center"
              letterSpacing={2}
              sx={{ display: { xs: 'block', sm: 'none' } }}
            >
              LEADERBOARD
            </Typography>
            <Typography
              variant={'caption'}
              textAlign={'center'}
              letterSpacing={3}
              display={'inline'}
              mt={1}
            >
              Total Players:
              <Typography
                variant={'subtitle1'}
                display={'inline'}
                fontWeight={600}
                ml={1}
              >
                {tournamentTotalPlayers}
              </Typography>
            </Typography>
            <PoolPlayLeaderboard
              cutoff={cutoff}
              gameOver={false}
              onUsernameClick={handlePoolLeaderboardUsernameClick}
              sortedPlayers={sortedPlayers}
              profileId={profileId}
              questionIndex={questionIndex}
              spectate={spectate}
            ></PoolPlayLeaderboard>
          </Drawer>
        ) : null}
        {tournament && tournamentTotalPlayers > 20 && sortedPlayers ? (
          <Drawer
            sx={{
              width: { xs: 144, sm: poolLeaderboardDrawerWidth },
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: { xs: 144, sm: poolLeaderboardDrawerWidth },
                boxSizing: 'border-box',
                bgcolor: '#333333',
                maxHeight: { xs: 416, sm: '100%' },
              },
            }}
            variant="permanent"
            anchor="right"
          >
            <Typography variant="h5" textAlign="center" letterSpacing={5}>
              LEADERBOARD
            </Typography>
            <TournamentSitAndGoLeaderboard
              cutoff={cutoff}
              onUsernameClick={handlePoolLeaderboardUsernameClick}
              profileId={profileId}
              questionIndex={questionIndex}
              sortedPlayers={sortedPlayers}
              spectate={spectate}
              tournamentTotalPlayers={tournamentTotalPlayers}
            />
          </Drawer>
        ) : null}
      </Box>
    </>
  );
};
